import type { Node } from "interfaces/schemas";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DistributiveOmit<T, K extends PropertyKey> = T extends any
  ? Omit<T, K>
  : never;

type NodeRequestItemFull = DistributiveOmit<Node, "nodes" | "partiallyVisible">;

export type NodeRequestItem = Partial<Omit<NodeRequestItemFull, "id">> & {
  id: string;
};

interface CreateNodesResult {
  nodesToRequest: NodeRequestItem[] | null;
  subTree: Node | null;
  error: Error | null;
}

interface CreateNodesInput {
  data: Node;
  callback?: (node: Node) => Node; // Optional callback to modify the node before it's added to the request list
}

export const createNodesInput = ({
  data,
  callback,
}: CreateNodesInput): CreateNodesResult => {
  const nodesToRequest: NodeRequestItem[] = [];

  const cloneNode = (node: Node, parentId: string | null): Node => {
    const newNode: Node = {
      ...node,
      parentId,
      id: crypto.randomUUID(),
    };

    const processedNode: Node = callback ? callback(newNode) : newNode;

    const {
      nodes: _nodes,
      partiallyVisible: _partiallyVisible,
      ...nodeRequestExtracted
    } = processedNode;
    const nodeRequest: NodeRequestItem = nodeRequestExtracted;
    nodesToRequest.push(nodeRequest);

    if (node.nodes) {
      processedNode.nodes = [
        ...node.nodes.map((child: Node) => cloneNode(child, processedNode.id)),
      ];
    }

    return processedNode;
  };

  try {
    const subTree = cloneNode(data, data.parentId || null);
    return {
      nodesToRequest,
      subTree,
      error: null,
    };
  } catch (error) {
    console.error("Error creating nodes:", error);
    return {
      nodesToRequest: null,
      subTree: null,
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred while creating nodes"),
    };
  }
};
